import * as FirebaseAuth from "firebase/auth";

import { auth } from "@/firebase/web";

export type PhoneVerificationErrorCode =
  | "NO_SIGNED_IN_USER"
  | "INVALID_PHONE"
  | "CAPTCHA_FAILED"
  | "TOO_MANY_REQUESTS"
  | "QUOTA_EXCEEDED"
  | "INVALID_CODE"
  | "CODE_EXPIRED"
  | "PHONE_ALREADY_IN_USE"
  | "SEND_FAILED"
  | "VERIFY_FAILED";

export class PhoneVerificationError extends Error {
  code: PhoneVerificationErrorCode;
  cause?: unknown;
  constructor(
    code: PhoneVerificationErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.name = "PhoneVerificationError";
    this.code = code;
    this.cause = cause;
  }
}

export type PhoneVerificationConfirmation = {
  confirm: (code: string) => Promise<void>;
};

const RECAPTCHA_CONTAINER_ID = "phone-verification-recaptcha";

let cachedVerifier: FirebaseAuth.RecaptchaVerifier | null = null;

export const clearCachedVerifier = () => {
  try {
    cachedVerifier?.clear();
  } catch {
    // ignore — verifier may already be torn down
  }
  cachedVerifier = null;
};

export const getOrCreateRecaptchaVerifier = (): FirebaseAuth.RecaptchaVerifier => {
  if (cachedVerifier) return cachedVerifier;

  if (typeof document === "undefined") {
    throw new PhoneVerificationError(
      "SEND_FAILED",
      "Phone verification is only available in a browser context.",
    );
  }

  let container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    container = document.createElement("div");
    container.id = RECAPTCHA_CONTAINER_ID;
    // Do NOT use `display: none` here: when reCAPTCHA Enterprise fails to
    // initialize, Firebase falls back to reCAPTCHA v2, which needs to mount
    // a real challenge widget anchored to this element. A hidden anchor
    // crashes the widget with "Cannot read properties of null (reading
    // 'style')". Park it off-screen instead so invisible mode stays
    // unobtrusive but v2 fallback can still render.
    container.style.position = "fixed";
    container.style.bottom = "0";
    container.style.right = "0";
    container.style.zIndex = "2147483647";
    document.body.appendChild(container);
  }

  cachedVerifier = new FirebaseAuth.RecaptchaVerifier(auth, container, {
    size: "invisible",
  });
  return cachedVerifier;
};

export const mapSendError = (error: unknown): PhoneVerificationError => {
  const code = (error as { code?: string })?.code;
  switch (code) {
    case "auth/invalid-phone-number":
      return new PhoneVerificationError(
        "INVALID_PHONE",
        "Invalid phone number.",
        error,
      );
    case "auth/captcha-check-failed":
      return new PhoneVerificationError(
        "CAPTCHA_FAILED",
        "reCAPTCHA verification failed.",
        error,
      );
    case "auth/too-many-requests":
      return new PhoneVerificationError(
        "TOO_MANY_REQUESTS",
        "Too many requests. Try again later.",
        error,
      );
    case "auth/quota-exceeded":
      return new PhoneVerificationError(
        "QUOTA_EXCEEDED",
        "SMS quota exceeded for this project.",
        error,
      );
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
    case "auth/provider-already-linked":
      return new PhoneVerificationError(
        "PHONE_ALREADY_IN_USE",
        "Phone number is already linked to another account.",
        error,
      );
    default:
      return new PhoneVerificationError(
        "SEND_FAILED",
        "Failed to send verification code.",
        error,
      );
  }
};

export const mapConfirmError = (error: unknown): PhoneVerificationError => {
  const code = (error as { code?: string })?.code;
  switch (code) {
    case "auth/invalid-verification-code":
      return new PhoneVerificationError(
        "INVALID_CODE",
        "Invalid verification code.",
        error,
      );
    case "auth/code-expired":
      return new PhoneVerificationError(
        "CODE_EXPIRED",
        "Verification code expired.",
        error,
      );
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return new PhoneVerificationError(
        "PHONE_ALREADY_IN_USE",
        "Phone number is already linked to another account.",
        error,
      );
    default:
      return new PhoneVerificationError(
        "VERIFY_FAILED",
        "Failed to verify code.",
        error,
      );
  }
};

export const startPhoneVerification = async (
  phoneE164: string,
): Promise<PhoneVerificationConfirmation> => {
  const user = auth.currentUser;
  if (!user) {
    throw new PhoneVerificationError(
      "NO_SIGNED_IN_USER",
      "Must be signed in to verify a phone number.",
    );
  }

  const verifier = getOrCreateRecaptchaVerifier();
  let verificationId: string;
  try {
    const provider = new FirebaseAuth.PhoneAuthProvider(auth);
    verificationId = await provider.verifyPhoneNumber(phoneE164, verifier);
  } catch (error) {
    clearCachedVerifier();
    throw mapSendError(error);
  }

  return {
    confirm: async (code: string) => {
      try {
        const credential = FirebaseAuth.PhoneAuthProvider.credential(
          verificationId,
          code,
        );
        if (user.phoneNumber) {
          await FirebaseAuth.updatePhoneNumber(user, credential);
        } else {
          await FirebaseAuth.linkWithCredential(user, credential);
        }
      } catch (error) {
        throw mapConfirmError(error);
      }
    },
  };
};
